/**
 * UIElement - Lightweight HTMLElement wrapper providing jQuery-like fluent API for DOM manipulation
 * with automatic event cleanup and parent-child relationship tracking.
 */

import { UIResource } from './UIResource.js';

export interface UIElementOptions {
  id?: string;
  className?: string;
  style?: Partial<CSSStyleDeclaration>;
  innerHTML?: string;
  textContent?: string;
  parent?: UIElement | string; // UIElement or selector
  tag?: keyof HTMLElementTagNameMap;
}

export class UIElement extends UIResource {
  public readonly element: HTMLElement;
  private children: UIElement[] = [];
  private eventListeners: Map<string, EventListener[]> = new Map();

  constructor(options: UIElementOptions = {}) {
    super();

    const tag = options.tag ?? 'div';
    this.element = document.createElement(tag);

    if (options.id) {
      this.element.id = options.id;
    }

    if (options.className) {
      this.element.className = options.className;
    }

    if (options.style) {
      Object.assign(this.element.style, options.style);
    }

    if (options.innerHTML) {
      this.element.innerHTML = options.innerHTML;
    } else if (options.textContent) {
      this.element.textContent = options.textContent;
    }

    if (options.parent) {
      this.appendTo(options.parent);
    }
  }

  public appendTo(parent: UIElement | string | HTMLElement): this {
    let parentElement: HTMLElement;

    if (typeof parent === 'string') {
      const found = document.querySelector(parent);
      if (!found) {
        throw new Error(`Parent element not found: ${parent}`);
      }
      parentElement = found as HTMLElement;
    } else if (parent instanceof UIElement) {
      parentElement = parent.element;
      parent.children.push(this);
    } else {
      parentElement = parent;
    }

    parentElement.appendChild(this.element);
    return this;
  }

  public remove(): void {
    // Remove all event listeners
    this.eventListeners.forEach((listeners, event) => {
      listeners.forEach(listener => {
        this.element.removeEventListener(event, listener);
      });
    });
    this.eventListeners.clear();

    // Remove all children
    this.children.forEach(child => child.remove());
    this.children = [];

    // Remove from DOM
    this.element.remove();

    this.unregisterFromUIManager();
  }

  public on(event: string, listener: EventListener): this {
    this.element.addEventListener(event, listener);

    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, []);
    }
      this.eventListeners.get(event)!.push(listener);

      return this;
  }

  public off(event: string, listener?: EventListener): this {
    if (listener) {
      this.element.removeEventListener(event, listener);
      const listeners = this.eventListeners.get(event);
      if (listeners) {
        const index = listeners.indexOf(listener);
        if (index > -1) {
          listeners.splice(index, 1);
        }
      }
    } else {
      // Remove all listeners for this event
      const listeners = this.eventListeners.get(event);
      if (listeners) {
        listeners.forEach(l => this.element.removeEventListener(event, l));
        this.eventListeners.delete(event);
      }
    }
    return this;
  }

  public show(): this {
    this.element.style.display = '';
    return this;
  }

  public hide(): this {
    this.element.style.display = 'none';
    return this;
  }

  public toggle(): this {
    if (this.element.style.display === 'none') {
      this.show();
    } else {
      this.hide();
    }
    return this;
  }

  public addClass(className: string): this {
    this.element.classList.add(className);
    return this;
  }

  public removeClass(className: string): this {
    this.element.classList.remove(className);
    return this;
  }

  public hasClass(className: string): boolean {
    return this.element.classList.contains(className);
  }

  public setStyle(property: keyof CSSStyleDeclaration, value: string): this {
    (this.element.style as any)[property] = value;
    return this;
  }

  public setText(text: string): this {
    this.element.textContent = text;
    return this;
  }

  public setHTML(html: string): this {
    this.element.innerHTML = html;
    return this;
  }

  public getAttribute(name: string): string | null {
    return this.element.getAttribute(name);
  }

  public setAttribute(name: string, value: string): this {
    this.element.setAttribute(name, value);
    return this;
  }

  public querySelector(selector: string): HTMLElement | null {
    return this.element.querySelector(selector);
  }

  public querySelectorAll(selector: string): NodeListOf<HTMLElement> {
    return this.element.querySelectorAll(selector);
  }
}
